import { TextInput } from "@/components/admin";
import { FileInput } from "@/components/admin/file-input";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { AttachmentField } from "../notes/AttachmentField";
import { VENDOR_DOCUMENT_CHECKLIST } from "./vendorDocumentChecklist";

const MANDATORY_COUNT = VENDOR_DOCUMENT_CHECKLIST.filter((d) => d.mandatory).length;

const FILE_ACCEPT = {
    "application/pdf": [".pdf"],
    "image/*": [".png", ".jpg", ".jpeg"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

/**
 * Vendor #23: fixed 28-row document checklist with per-document upload + remarks.
 *
 * Rows render directly from the static checklist (always visible). File + remarks
 * bind to `documents.<index>.*`; doc_type/doc_name/mandatory and any already-saved
 * file_url are pre-loaded into form state by index, and the dataProvider's vendor
 * `beforeSave` re-derives the metadata from the checklist before persisting.
 */
export const VendorDocumentsInput = () => {
    const { setValue, getValues } = useFormContext();
    const watchedDocs = useWatch({ name: "documents" }) ?? [];

    // Live count of mandatory docs that have a file (new upload or already saved).
    const mandatorySatisfied = VENDOR_DOCUMENT_CHECKLIST.reduce((n, item, i) => {
        if (!item.mandatory) return n;
        const row: any = Array.isArray(watchedDocs) ? watchedDocs[i] : undefined;
        return row?.file?.rawFile || row?.file_url ? n + 1 : n;
    }, 0);
    const allMandatoryDone = mandatorySatisfied >= MANDATORY_COUNT;

    // Pre-load saved values (file_url / remarks) into the indexed form fields so
    // they show up when editing an existing vendor.
    useEffect(() => {
        const existing = getValues("documents");
        if (!Array.isArray(existing) || existing.length === 0) return;
        const byType = new Map(existing.map((d: any) => [d.doc_type, d]));
        VENDOR_DOCUMENT_CHECKLIST.forEach((item, i) => {
            const prev: any = byType.get(item.doc_type);
            if (!prev) return;
            if (prev.file_url) setValue(`documents.${i}.file_url`, prev.file_url, { shouldDirty: false });
            if (prev.remarks) setValue(`documents.${i}.remarks`, prev.remarks, { shouldDirty: false });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="space-y-2">
            <div
                className={`flex items-center justify-between rounded-md border px-3 py-2 text-xs font-semibold ${
                    allMandatoryDone
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-amber-200 bg-amber-50 text-amber-700"
                }`}
            >
                <span>Required documents: {mandatorySatisfied}/{MANDATORY_COUNT} uploaded</span>
                <span className="font-normal text-[11px] opacity-80">
                    {allMandatoryDone
                        ? "Ready for approval"
                        : "Vendor can be saved now; approval is blocked until all required docs are uploaded"}
                </span>
            </div>
            {VENDOR_DOCUMENT_CHECKLIST.map((item, i) => {
                const savedUrl = getValues(`documents.${i}.file_url`);
                return (
                    <div
                        key={item.doc_type}
                        className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start p-3 rounded-lg border bg-slate-50/40"
                    >
                        <div className="md:col-span-4 flex items-start gap-2 pt-2">
                            <span className="text-xs font-medium text-slate-700">
                                {i + 1}. {item.doc_name}
                            </span>
                            {item.mandatory && (
                                <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 shrink-0">
                                    Required
                                </span>
                            )}
                        </div>
                        <div className="md:col-span-5">
                            {savedUrl && (
                                <a
                                    href={savedUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-[#00a68d] hover:underline block mb-1"
                                >
                                    View uploaded file
                                </a>
                            )}
                            <FileInput source={`documents.${i}.file`} label={false} accept={FILE_ACCEPT}>
                                <AttachmentField source="src" title="title" target="_blank" />
                            </FileInput>
                        </div>
                        <div className="md:col-span-3">
                            <TextInput
                                source={`documents.${i}.remarks`}
                                label={false}
                                placeholder="Remarks"
                                helperText={false}
                                className="w-full"
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
